/// <amd-module name="TFS/Services/TfsRestErrorHandlerService" />

import * as React from "react";

import { IVssPageContext, RestErrorHandlerResponse, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IWebContributionsRestClient } from "VSS/Platform/RestClient/WebContributions.Types";
import { publishEvent } from "VSS/Platform/Telemetry";
import { ReauthenticationDialog } from "./ReauthenticationDialog";
import { ITfsRestErrorHandlerService, PageType } from "./TfsRestErrorHandlerService.types";

const traceArea = "TfsRestErrorHandlerService";
const fetchSignoutFeatureFlag = "VisualStudio.Services.WebAccess.RestErrorHandler.FetchSignout";

class TfsRestErrorHandlerService extends VssService implements ITfsRestErrorHandlerService {
    public _serviceStart(context: IVssPageContext) {
        super._serviceStart(context);
    }

    public async handleError(
        tries: number,
        text: string | undefined,
        response: Response | undefined,
        error: Error
    ): Promise<RestErrorHandlerResponse> {
        const url = response && new URL(response.url);
        return this._handleError(tries, text, url, error, "newplat");
    }

    public async handleOldPlatError(
        tries: number,
        text: string | undefined,
        xhr: XMLHttpRequest | undefined,
        error: Error
    ): Promise<RestErrorHandlerResponse> {
        const url = xhr && new URL(xhr.responseURL);
        return this._handleError(tries, text, url, error, "oldplat");
    }

    public verify(): Promise<{ success: boolean; newUrl?: string }> {
        return new Promise<{ success: boolean; newUrl?: string }>((resolve, reject) => {
            console.log("Verifying authentication.");
            const dataProvider = "ms.vss-web.user-claims-data";

            // try to query this data provider to see if we're authenticated
            const webContributionsClient = this.pageContext.getRestClient<IWebContributionsRestClient>("IWebContributionsRestClient", {
                errorHandler: async (
                    pageContext: IVssPageContext,
                    tries: number,
                    text: string | undefined,
                    response: Response | undefined,
                    error: Error
                ) => {
                    // if there was an error we want to communicate the URL we were potentially redirected to
                    console.log(`Handling error in verification, new url ${response?.url}`);
                    resolve({ success: false, newUrl: response?.url });
                    return RestErrorHandlerResponse.Continue;
                },
                ignoreQueue: true // need to bypass the queue of requests waiting for us to complete reauthentication
            });

            webContributionsClient
                .queryContributedHierarchy(
                    {
                        contributionIds: [dataProvider],
                        dataProviderContext: { properties: {} }
                    },
                    undefined,
                    undefined
                )
                .then(
                    (result) => {
                        console.log(`Verification result: ${JSON.stringify(result)}`);
                        if (result.dataProviders[dataProvider].member) {
                            // succesfully authenticated
                            // TODO: should we check to see that they reauthenticated as the same user?
                            resolve({ success: true });
                        } else {
                            resolve({ success: false });
                        }
                    },
                    (err) => {
                        console.error(`Exception in authentication verification: ${err}`);
                        // the error handler defined above likely already caught this, so the promise is already
                        // resolved, so this will be a no-op, which is fine.
                        reject(err);
                    }
                );
        });
    }

    public getPageType(url: URL): PageType {
        switch (url.pathname) {
            case "/_signin":
                return "signin";
            case "/_signout":
                return "signout";
            default:
                return "other";
        }
    }

    public getSigninUrl(signinUrl: URL): URL {
        // We open a popup and send it to openPopupUrl
        // the code in openPopupUrl redirects the window to the url that will authenticate the user
        // the authentication process redirects back to closePopupUrl
        // the code in closePopupUrl closes the window
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = contributionService.getData<{ openPopupUrl: string; closePopupUrl: string }>(
            "ms.vss-tfs-web.tfs-rest-error-handler-service-data-provider"
        )!;

        const authenticationUrl = new URL(signinUrl.href);
        authenticationUrl.searchParams.set("force", "1");
        authenticationUrl.searchParams.set("reply_to", data.closePopupUrl);

        const openPopupUrl = new URL(data.openPopupUrl);
        openPopupUrl.searchParams.set("r", authenticationUrl.href);

        return openPopupUrl;
    }

    private async _handleError(
        tries: number,
        text: string | undefined,
        responseUrl: URL | undefined,
        error: Error,
        source: "oldplat" | "newplat" | "verify"
    ): Promise<RestErrorHandlerResponse> {
        // When authentication is expired, we expect to get redirect to /_signout or /_signin on an SPS instance
        // url will look something like
        // https://spsprodsbr1.vssps.visualstudio.com/_signin?realm=dev.azure.com&reply_to=[url]&redirect=1&hid=[guid]&context=[stuff]
        // or
        // https://spsprodsbr1.vssps.visualstudio.com/_signout?forceSignout=true&redirectUrl=[url]
        // and the JSON parsing will fail with a SyntaxError: Unexpected token < in JSON at position 4.
        // We try not to look at the message too hard because it may be localized into the user's language.
        if (!responseUrl) {
            return RestErrorHandlerResponse.Continue;
        }

        let pageType = this.getPageType(responseUrl);

        if (
            pageType !== "other" &&
            tries === 1 &&
            (error.name === "SyntaxError" || (error.name === "TFS.WebApi.Exception" && error.message?.includes("parsererror")))
        ) {
            publishEvent(this.pageContext, traceArea, "handleErrorStart", {
                tries,
                responseUrl: responseUrl.href,
                errorName: error.name,
                errorMessage: error.message,
                source
            });

            if (pageType === "signout" && isFeatureFlagEnabled(this.pageContext, fetchSignoutFeatureFlag)) {
                // load the signout URL with fetch instead of ignoring it
                const fetchUrl = this.getSigninUrl(responseUrl);
                // federated mode just returns an empty page
                fetchUrl.searchParams.set("mode", "federated");
                const response = await fetch(fetchUrl.href);
                publishEvent(this.pageContext, traceArea, "handleErrorFetch", {
                    status: response.status,
                    statusText: response.statusText
                });
            }

            if (pageType === "signout") {
                // we have a signout url, so let's make another request to try to get a signin url
                const result = await this.verify();
                publishEvent(this.pageContext, traceArea, "handleErrorSkip", {
                    originalUrl: responseUrl.href,
                    result
                });
                if (result.success) {
                    // unexpected, but it's possible the situation has been resolved
                    return RestErrorHandlerResponse.Retry;
                }
                if (result.newUrl) {
                    responseUrl = new URL(result.newUrl);
                    pageType = this.getPageType(responseUrl);
                }
            }

            if (pageType !== "signin") {
                // the popup only knows how to handle signin urls
                return RestErrorHandlerResponse.Continue;
            }

            const popupUrl = this.getSigninUrl(responseUrl);
            const result = await this._showReauthenticationDialog(popupUrl.href);

            publishEvent(this.pageContext, traceArea, "handleErrorResponse", {
                result,
                tries,
                responseUrl: responseUrl.href,
                popupUrl: popupUrl.href,
                errorName: error.name,
                errorMessage: error.message,
                source
            });
            return result;
        } else {
            return RestErrorHandlerResponse.Continue;
        }
    }

    private _showReauthenticationDialog(popupUrl: string): Promise<RestErrorHandlerResponse> {
        return new Promise<RestErrorHandlerResponse>((resolve, reject) => {
            const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.renderCallout((onDismiss) => (
                <ReauthenticationDialog onDismiss={onDismiss} onReject={reject} onResolve={resolve} popupUrl={popupUrl} />
            ));
        });
    }
}

Services.add("ITfsRestErrorHandlerService", { serviceFactory: TfsRestErrorHandlerService });
